import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { fetchAuditoriaCompletaSellersApi } from '../../api/axios';
import BigSpinner from '../General/BigSpinner';
import SafeReactPaginate from '../General/SafeReactPaginate';
import '../General/AdminListCard.css';
import '../Publicaciones/Components/ABM/AuditoriaPrecioStock.css';

const DEFAULT_PAGE_SIZE = 10;
const DAYS_OPTIONS = [
    { value: '30', label: '30 días' },
    { value: '60', label: '60 días' },
    { value: '90', label: '90 días' }
];

const buildInitialFilters = () => ({
    idSeller: '',
    dias: '30'
});

const toArray = (...values) => {
    for (const value of values) {
        if (Array.isArray(value)) return value;
    }
    return [];
};

const toNumberOrUndefined = (value) => {
    if (value === '' || value == null) return undefined;
    const parsed = Number.parseInt(String(value).trim(), 10);
    return Number.isNaN(parsed) ? undefined : parsed;
};

const toNumberOrNull = (value) => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const normalizeDaysFilter = (value) => (
    DAYS_OPTIONS.some((option) => option.value === String(value)) ? String(value) : '30'
);

const toMeaningfulValue = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized || null;
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short',
        timeStyle: 'short'
    }).format(date);
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' }).format(date);
};

/** Fecha legible para eventos (día + mes + año + hora separados). */
const getReadableDateParts = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return { fallback: String(value) };
    }
    const weekday = new Intl.DateTimeFormat('es-AR', { weekday: 'long' }).format(date);
    const dayMonthYear = new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    }).format(date);
    const time = new Intl.DateTimeFormat('es-AR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    }).format(date);
    return {
        weekday: weekday.charAt(0).toUpperCase() + weekday.slice(1),
        dayMonthYear,
        time
    };
};

const formatReadablePeriodDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    }).format(date);
};

const formatTextValue = (value) => {
    const normalized = String(value ?? '').trim();
    return normalized || '-';
};

const hasMeaningfulText = (value) => Boolean(String(value ?? '').trim());

const integerFormatter = new Intl.NumberFormat('es-AR');

const formatInteger = (value) => {
    if (value == null) return '-';
    return integerFormatter.format(value);
};

const getEstadoPillClass = (estado) => {
    const normalized = String(estado ?? '').trim().toLowerCase();
    if (normalized === 'activo' || normalized === 'true' || normalized === '1' || normalized === 'si' || normalized === 'sí') {
        return 'auditoria-publicacion__estado-pill auditoria-publicacion__estado-pill--success';
    }
    if (normalized === 'inactivo' || normalized === 'false' || normalized === '0' || normalized === 'no' || normalized === 'baja') {
        return 'auditoria-publicacion__estado-pill auditoria-publicacion__estado-pill--error';
    }
    return 'auditoria-publicacion__estado-pill';
};

const getAccionPillClass = (accion) => {
    const normalized = String(accion ?? '').trim().toLowerCase();
    if (normalized === 'alta' || normalized === 'create' || normalized === 'creación' || normalized === 'creacion') {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--alta';
    }
    if (
        normalized === 'edición'
        || normalized === 'edicion'
        || normalized === 'update'
        || normalized === 'modificación'
        || normalized === 'modificacion'
    ) {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--edicion';
    }
    if (
        normalized === 'baja'
        || normalized === 'delete'
        || normalized === 'deshabilitación'
        || normalized === 'deshabilitacion'
    ) {
        return 'auditoria-publicacion__accion-pill auditoria-publicacion__accion-pill--baja';
    }
    return 'auditoria-publicacion__accion-pill';
};

const EventDateBlock = ({ value }) => {
    const parts = getReadableDateParts(value);
    if (!parts) {
        return <span className="auditoria-sellers__event-date-fallback">—</span>;
    }
    if (parts.fallback) {
        return <span className="auditoria-sellers__event-date-fallback">{parts.fallback}</span>;
    }
    return (
        <time className="auditoria-sellers__event-date" dateTime={value}>
            <span className="auditoria-sellers__event-date-weekday">{parts.weekday}</span>
            <span className="auditoria-sellers__event-date-day">{parts.dayMonthYear}</span>
            <span className="auditoria-sellers__event-date-time">{parts.time} hs</span>
        </time>
    );
};

const EventChangeValue = ({ label, value, tone }) => {
    const text = formatTextValue(value);
    const isEmpty = text === '-';
    return (
        <div className={`auditoria-sellers__event-value auditoria-sellers__event-value--${tone}`}>
            <span className="auditoria-sellers__event-value-label">{label}</span>
            <strong
                className={`auditoria-sellers__event-value-text${isEmpty ? ' auditoria-sellers__event-value-text--empty' : ''}`}
            >
                {text}
            </strong>
        </div>
    );
};

const SellerEventCard = ({ evento }) => {
    const hasObs = hasMeaningfulText(evento.observacion);
    return (
        <article className="auditoria-sellers__event-card">
            <div className="auditoria-sellers__event-card-when">
                <EventDateBlock value={evento.fecha} />
                <span className={getAccionPillClass(evento.accion)}>
                    {formatTextValue(evento.accion)}
                </span>
            </div>

            <div className="auditoria-sellers__event-card-main">
                <div className="auditoria-sellers__event-meta-row" aria-label="Campo y usuario">
                    <div className="auditoria-sellers__campo-chip">
                        <span className="auditoria-sellers__chip-label">Campo modificado</span>
                        <span className="auditoria-sellers__chip-value">{formatTextValue(evento.campo)}</span>
                    </div>
                    <div className="auditoria-sellers__usuario-chip">
                        <span className="auditoria-sellers__chip-label">Usuario auditoría</span>
                        <span className="auditoria-sellers__chip-value">
                            {formatTextValue(evento.usuario)}
                            {evento.idUsuario != null ? (
                                <span className="auditoria-sellers__chip-id">#{evento.idUsuario}</span>
                            ) : null}
                        </span>
                    </div>
                </div>

                <div className="auditoria-sellers__event-change" aria-label="Cambio de valor">
                    <EventChangeValue label="Antes" value={evento.valorAnterior} tone="antes" />
                    <span className="auditoria-sellers__event-change-arrow" aria-hidden="true">→</span>
                    <EventChangeValue label="Después" value={evento.valorActual} tone="despues" />
                </div>

                {hasObs ? (
                    <div className="auditoria-sellers__event-obs">
                        <span className="auditoria-sellers__event-obs-label">Observación</span>
                        <p className="auditoria-sellers__event-obs-text mb-0">
                            {String(evento.observacion).trim()}
                        </p>
                    </div>
                ) : null}
            </div>
        </article>
    );
};

const sellerHeaderTooltips = {
    seller: 'Identificador y razón social del seller auditado.',
    cuit: 'CUIT informado en el resumen de auditoría.',
    estado: 'Estado actual del seller al momento de la consulta.',
    integrador: 'Integrador vigente asociado al seller.',
    movimientos: 'Cantidad total de movimientos en el período consultado.',
    ultimoMovimiento: 'Fecha del último movimiento registrado en el rango.',
    periodo: 'Cada fila agrupa los movimientos del seller dentro del rango auditado.',
    camposModificados: 'Campos del seller que registraron cambios dentro del período.',
    usuarios: 'Usuarios que participaron en modificaciones dentro del período.'
};

const HeaderWithTooltip = ({ label, tooltip }) => (
    <span className="auditoria-publicacion__header-label">
        <span>{label}</span>
        {tooltip ? (
            <span
                className="auditoria-publicacion__header-info"
                title={tooltip}
                aria-label={`${label}: ${tooltip}`}
                tabIndex="0"
            >
                i
            </span>
        ) : null}
    </span>
);

const normalizeEvento = (evento, index) => ({
    id: evento?.idMovimiento ?? evento?.IdMovimiento ?? `${evento?.fecha ?? evento?.Fecha ?? 'evento'}-${index}`,
    fecha: evento?.fecha ?? evento?.Fecha ?? null,
    idUsuario: toNumberOrNull(evento?.idUsuario ?? evento?.IdUsuario),
    accion: evento?.accion ?? evento?.Accion ?? '-',
    campo: evento?.campo ?? evento?.Campo ?? '-',
    valorAnterior: evento?.valorAnterior ?? evento?.ValorAnterior ?? null,
    valorActual: evento?.valorActual ?? evento?.ValorActual ?? null,
    usuario: evento?.usuario ?? evento?.Usuario ?? '-',
    observacion: evento?.observacion ?? evento?.Observacion ?? ''
});

const normalizePeriodo = (periodo, index) => {
    const eventos = toArray(periodo?.eventos, periodo?.Eventos).map(normalizeEvento);
    const camposModificados = toArray(periodo?.camposModificados, periodo?.CamposModificados)
        .map((campo) => toMeaningfulValue(campo))
        .filter(Boolean);
    const usuarios = toArray(periodo?.usuarios, periodo?.Usuarios)
        .map((usuario) => toMeaningfulValue(usuario))
        .filter(Boolean);

    return {
        id: periodo?.id ?? periodo?.Id ?? `${periodo?.fechaDesde ?? periodo?.FechaDesde ?? 'periodo'}-${index}`,
        fechaDesde: periodo?.fechaDesde ?? periodo?.FechaDesde ?? null,
        fechaHasta: periodo?.fechaHasta ?? periodo?.FechaHasta ?? null,
        cantidadMovimientos: toNumberOrNull(periodo?.cantidadMovimientos ?? periodo?.CantidadMovimientos) ?? eventos.length,
        camposModificados,
        usuarios,
        eventos
    };
};

const normalizeSellerItem = (item, index) => {
    const resumen = item?.resumen ?? item?.Resumen ?? {};
    const periodos = toArray(item?.periodos, item?.Periodos).map(normalizePeriodo);
    const idSeller = toNumberOrNull(resumen?.idSeller ?? resumen?.IdSeller) ?? index;

    return {
        id: idSeller,
        resumen: {
            idSeller,
            razonSocial: resumen?.razonSocial ?? resumen?.RazonSocial ?? '',
            cuit: resumen?.cuit ?? resumen?.Cuit ?? '',
            integradorActual: resumen?.integradorActual ?? resumen?.IntegradorActual ?? '',
            estadoActual: resumen?.estadoActual ?? resumen?.EstadoActual ?? '',
            usuarioActual: resumen?.usuarioActual ?? resumen?.UsuarioActual ?? '',
            emailUsuarioActual: resumen?.emailUsuarioActual ?? resumen?.EmailUsuarioActual ?? '',
            fechaDesde: resumen?.fechaDesde ?? resumen?.FechaDesde ?? null,
            fechaHasta: resumen?.fechaHasta ?? resumen?.FechaHasta ?? null,
            diasConsultados: toNumberOrNull(resumen?.diasConsultados ?? resumen?.DiasConsultados) ?? 0,
            cantidadMovimientos: toNumberOrNull(resumen?.cantidadMovimientos ?? resumen?.CantidadMovimientos)
                ?? periodos.reduce((acc, periodo) => acc + (periodo?.cantidadMovimientos ?? 0), 0),
            cantidadAltas: toNumberOrNull(resumen?.cantidadAltas ?? resumen?.CantidadAltas) ?? 0,
            cantidadModificaciones: toNumberOrNull(resumen?.cantidadModificaciones ?? resumen?.CantidadModificaciones) ?? 0,
            fechaUltimoMovimiento: resumen?.fechaUltimoMovimiento ?? resumen?.FechaUltimoMovimiento ?? null
        },
        periodos
    };
};

const normalizeResponse = (raw) => {
    const data = raw?.data ?? raw ?? {};
    const sellers = toArray(
        data?.listMovimientosCompletosPorSellers,
        data?.ListMovimientosCompletosPorSellers
    ).map(normalizeSellerItem);

    return { sellers };
};

const buildRequest = ({ filters }) => {
    const request = {};
    const idSeller = toNumberOrUndefined(filters.idSeller);
    const dias = toNumberOrUndefined(normalizeDaysFilter(filters.dias));

    if (idSeller != null) request.IdSeller = idSeller;
    if (dias != null) request.Dias = dias;

    return request;
};

const appendWorkbookSheet = (workbook, name, rows, cols = []) => {
    const worksheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
    if (cols.length) worksheet['!cols'] = cols;
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
};

const buildExcelFileName = (dias) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return `auditoria-sellers-${dias || 30}d-${timestamp}.xlsx`;
};

function AuditoriaSellers() {
    const navigate = useNavigate();
    const [filters, setFilters] = useState(buildInitialFilters);
    const [appliedFilters, setAppliedFilters] = useState(buildInitialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [response, setResponse] = useState(() => normalizeResponse({}));
    const [expandedSellerId, setExpandedSellerId] = useState(null);
    const [expandedPeriodId, setExpandedPeriodId] = useState(null);

    useEffect(() => {
        const fetchAuditoria = async () => {
            setLoading(true);
            setError('');
            setExpandedSellerId(null);
            setExpandedPeriodId(null);

            try {
                const rawData = await fetchAuditoriaCompletaSellersApi(
                    buildRequest({ filters: appliedFilters })
                );
                setResponse(normalizeResponse(rawData));
            } catch (err) {
                setResponse(normalizeResponse({}));
                setError(err?.response?.data?.mensaje || 'No se pudo cargar la auditoría de sellers.');
            } finally {
                setLoading(false);
            }
        };

        fetchAuditoria();
    }, [appliedFilters]);

    const sellers = useMemo(() => response.sellers, [response.sellers]);
    const totalPages = Math.max(1, Math.ceil(sellers.length / Math.max(1, pageSize)));

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const paginatedSellers = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return sellers.slice(start, start + pageSize);
    }, [currentPage, sellers, pageSize]);

    const summaryCards = useMemo(() => {
        const totalMovimientos = sellers.reduce(
            (acc, seller) => acc + (seller.resumen.cantidadMovimientos ?? 0),
            0
        );
        const totalAltas = sellers.reduce(
            (acc, seller) => acc + (seller.resumen.cantidadAltas ?? 0),
            0
        );
        const totalModificaciones = sellers.reduce(
            (acc, seller) => acc + (seller.resumen.cantidadModificaciones ?? 0),
            0
        );
        const conMovimientos = sellers.filter((seller) => (seller.resumen.cantidadMovimientos ?? 0) > 0).length;

        return [
            { label: 'Sellers', value: formatInteger(sellers.length) },
            { label: 'Con movimientos', value: formatInteger(conMovimientos) },
            { label: 'Movimientos', value: formatInteger(totalMovimientos) },
            { label: 'Altas', value: formatInteger(totalAltas) },
            { label: 'Modificaciones', value: formatInteger(totalModificaciones) }
        ];
    }, [sellers]);

    const rangoDesde = sellers.length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangoHasta = sellers.length === 0
        ? 0
        : Math.min(currentPage * pageSize, sellers.length);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFilters((previous) => ({
            ...previous,
            [name]: value
        }));
    };

    const handleDaysChange = (dias) => {
        setFilters((previous) => ({
            ...previous,
            dias
        }));
    };

    const handleApplyFilters = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        setAppliedFilters({
            ...filters,
            dias: normalizeDaysFilter(filters.dias)
        });
    };

    const handleResetFilters = (event) => {
        event.preventDefault();
        const nextFilters = buildInitialFilters();
        setFilters(nextFilters);
        setAppliedFilters(nextFilters);
        setCurrentPage(1);
        setPageSize(DEFAULT_PAGE_SIZE);
        setExpandedSellerId(null);
        setExpandedPeriodId(null);
    };

    const handlePageClick = (data) => {
        setCurrentPage(data.selected + 1);
        setExpandedSellerId(null);
        setExpandedPeriodId(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleSeller = (sellerId) => {
        setExpandedSellerId((previous) => (previous === sellerId ? null : sellerId));
        setExpandedPeriodId(null);
    };

    const togglePeriodo = (periodoId) => {
        setExpandedPeriodId((previous) => (previous === periodoId ? null : periodoId));
    };

    const handleExportExcel = () => {
        if (!sellers.length || exporting) return;

        setExporting(true);
        try {
            const workbook = XLSX.utils.book_new();
            const dias = normalizeDaysFilter(appliedFilters.dias);

            const resumenRows = sellers.map((seller) => ({
                'Id Seller': seller.resumen.idSeller,
                'Razón social': seller.resumen.razonSocial,
                CUIT: seller.resumen.cuit,
                Estado: seller.resumen.estadoActual,
                Integrador: seller.resumen.integradorActual,
                'Usuario actual': seller.resumen.usuarioActual,
                Email: seller.resumen.emailUsuarioActual,
                Movimientos: seller.resumen.cantidadMovimientos,
                Altas: seller.resumen.cantidadAltas,
                Modificaciones: seller.resumen.cantidadModificaciones,
                'Fecha desde': formatDateTime(seller.resumen.fechaDesde),
                'Fecha hasta': formatDateTime(seller.resumen.fechaHasta),
                'Último movimiento': formatDateTime(seller.resumen.fechaUltimoMovimiento),
                'Días consultados': seller.resumen.diasConsultados
            }));

            const periodoRows = sellers.flatMap((seller) => (
                seller.periodos.map((periodo) => ({
                    'Id Seller': seller.resumen.idSeller,
                    'Razón social': seller.resumen.razonSocial,
                    'Fecha desde': formatDate(periodo.fechaDesde),
                    'Fecha hasta': formatDate(periodo.fechaHasta),
                    Movimientos: periodo.cantidadMovimientos,
                    'Campos modificados': periodo.camposModificados.join(', '),
                    Usuarios: periodo.usuarios.join(', ')
                }))
            ));

            const eventoRows = sellers.flatMap((seller) => (
                seller.periodos.flatMap((periodo) => (
                    periodo.eventos.map((evento) => ({
                        'Id Seller': seller.resumen.idSeller,
                        'Razón social': seller.resumen.razonSocial,
                        'Período desde': formatDate(periodo.fechaDesde),
                        'Período hasta': formatDate(periodo.fechaHasta),
                        Fecha: formatDateTime(evento.fecha),
                        Acción: evento.accion,
                        Campo: evento.campo,
                        Usuario: evento.usuario,
                        'Id usuario': evento.idUsuario,
                        Antes: evento.valorAnterior,
                        Después: evento.valorActual,
                        Observación: evento.observacion
                    }))
                ))
            ));

            appendWorkbookSheet(workbook, 'Resumen sellers', resumenRows, [
                { wch: 12 }, { wch: 32 }, { wch: 14 }, { wch: 12 }, { wch: 18 },
                { wch: 22 }, { wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 14 },
                { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 14 }
            ]);
            appendWorkbookSheet(workbook, 'Periodos', periodoRows.length ? periodoRows : [{ Info: 'Sin períodos' }], [
                { wch: 12 }, { wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 36 }, { wch: 28 }
            ]);
            appendWorkbookSheet(workbook, 'Eventos', eventoRows.length ? eventoRows : [{ Info: 'Sin eventos' }], [
                { wch: 12 }, { wch: 32 }, { wch: 14 }, { wch: 14 }, { wch: 18 },
                { wch: 14 }, { wch: 18 }, { wch: 22 }, { wch: 12 }, { wch: 22 },
                { wch: 22 }, { wch: 28 }
            ]);

            XLSX.writeFile(workbook, buildExcelFileName(dias));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="container py-3 auditoria-modulo">
            <div className="row mb-3 mb-md-4 align-items-center py-3 px-2 rounded admin-page-heading auditoria-page-heading">
                <div className="col">
                    <h4 className="mb-0 text-themecolor color-rojo-megatone fw-bold">
                        Auditoría de sellers
                    </h4>
                    <p className="small text-body-secondary mb-0 mt-1">
                        Historial completo de altas y modificaciones por seller, con detalle por período y evento.
                    </p>
                </div>
            </div>

            <div className="auditoria-publicacion">
                <div className="auditoria-publicacion__metrics">
                    {summaryCards.map((card) => (
                        <div key={card.label} className="auditoria-publicacion__card">
                            <strong>{card.value}</strong>
                            <span className="auditoria-publicacion__label">{card.label}</span>
                        </div>
                    ))}
                </div>

                <form onSubmit={handleApplyFilters} onReset={handleResetFilters}>
                    <div className="auditoria-publicacion__filters-card">
                        <div className="auditoria-publicacion__filters-header">
                            <h6 className="auditoria-publicacion__filters-title">
                                <i className="fa fa-sliders" aria-hidden="true" />
                                Controles de consulta
                            </h6>
                        </div>
                        <div className="auditoria-publicacion__filters-body">
                            <div className="auditoria-publicacion__toolbar">
                                <div className="auditoria-sellers__filter-field">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Id Seller</p>
                                    <input
                                        type="number"
                                        className="form-control auditoria-sellers__id-input"
                                        name="idSeller"
                                        value={filters.idSeller}
                                        onChange={handleInputChange}
                                        placeholder="Todos"
                                        min="1"
                                        title="Dejá vacío para consultar todos los sellers con movimientos."
                                    />
                                </div>

                                <div className="auditoria-sellers__filter-field">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Período</p>
                                    <div className="auditoria-publicacion__months">
                                        {DAYS_OPTIONS.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`auditoria-publicacion__filter-btn ${normalizeDaysFilter(filters.dias) === option.value ? 'is-active' : ''}`}
                                                onClick={() => handleDaysChange(option.value)}
                                                title={`Consultar movimientos de los últimos ${option.label}.`}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="auditoria-sellers__filter-field">
                                    <p className="auditoria-publicacion__filtro-label mb-0">Por página</p>
                                    <select
                                        className="form-control auditoria-sellers__page-size"
                                        value={pageSize}
                                        onChange={(event) => {
                                            setPageSize(Number.parseInt(event.target.value, 10));
                                            setCurrentPage(1);
                                            setExpandedSellerId(null);
                                            setExpandedPeriodId(null);
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                    </select>
                                </div>

                                <div className="auditoria-publicacion__toolbar-actions">
                                    <button
                                        type="submit"
                                        className="auditoria-publicacion__filter-btn auditoria-publicacion__filter-btn--primary"
                                        disabled={loading}
                                    >
                                        {loading ? 'Buscando...' : 'Buscar'}
                                    </button>
                                    <button type="reset" className="auditoria-publicacion__filter-btn">
                                        Limpiar
                                    </button>
                                    <button
                                        type="button"
                                        className="auditoria-publicacion__export-btn"
                                        onClick={handleExportExcel}
                                        disabled={loading || exporting || !!error || !sellers.length}
                                        title="Descarga un Excel con resumen, períodos y eventos de la auditoría."
                                    >
                                        {exporting ? 'Exportando...' : 'Exportar Excel'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

                <div className="auditoria-publicacion__info auditoria-publicacion__info--note">
                    <span>
                        Se muestran hasta <strong>{normalizeDaysFilter(appliedFilters.dias)} días</strong> del historial
                        auditado
                        {appliedFilters.idSeller
                            ? <> para el seller <strong>#{appliedFilters.idSeller}</strong></>
                            : ' de todos los sellers con movimientos'}
                        .
                    </span>
                    {sellers.length > 0 && (
                        <span>
                            Mostrando <strong>{rangoDesde}-{rangoHasta}</strong> de{' '}
                            <strong>{sellers.length}</strong> resultados
                        </span>
                    )}
                </div>

                {error ? (
                    <div className="auditoria-publicacion__state auditoria-publicacion__state--error" role="alert">
                        {error}
                    </div>
                ) : null}

                {loading ? (
                    <BigSpinner message="Cargando auditoría de sellers..." />
                ) : !error && paginatedSellers.length === 0 ? (
                    <div className="auditoria-publicacion__state">
                        No se encontraron movimientos para los filtros seleccionados.
                    </div>
                ) : !error ? (
                    <>
                        <div className="auditoria-publicacion__results-card">
                            <div className="auditoria-publicacion__results-header">
                                <h5 className="auditoria-publicacion__results-title mb-0">Sellers auditados</h5>
                                <span className="auditoria-publicacion__results-badge">
                                    {paginatedSellers.length} en esta página
                                </span>
                            </div>
                            <div className="auditoria-publicacion__results-body">
                                <div className="table-responsive">
                                    <table className="table auditoria-publicacion__table mb-0">
                                        <thead>
                                            <tr>
                                                <th scope="col">
                                                    <HeaderWithTooltip label="Seller" tooltip={sellerHeaderTooltips.seller} />
                                                </th>
                                                <th scope="col">
                                                    <HeaderWithTooltip label="CUIT" tooltip={sellerHeaderTooltips.cuit} />
                                                </th>
                                                <th scope="col">
                                                    <HeaderWithTooltip label="Estado" tooltip={sellerHeaderTooltips.estado} />
                                                </th>
                                                <th scope="col">
                                                    <HeaderWithTooltip label="Integrador" tooltip={sellerHeaderTooltips.integrador} />
                                                </th>
                                                <th scope="col">
                                                    <HeaderWithTooltip label="Movimientos" tooltip={sellerHeaderTooltips.movimientos} />
                                                </th>
                                                <th scope="col">
                                                    <HeaderWithTooltip label="Último movimiento" tooltip={sellerHeaderTooltips.ultimoMovimiento} />
                                                </th>
                                                <th scope="col" className="text-end">Detalle</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedSellers.map((seller) => {
                                                const { resumen, periodos } = seller;
                                                const expanded = expandedSellerId === seller.id;
                                                const sellerDetailCards = [
                                                    { label: 'Altas', value: formatInteger(resumen.cantidadAltas) },
                                                    { label: 'Modificaciones', value: formatInteger(resumen.cantidadModificaciones) },
                                                    { label: 'Períodos', value: formatInteger(periodos.length) },
                                                    { label: 'Días', value: formatInteger(resumen.diasConsultados || Number(appliedFilters.dias)) }
                                                ];

                                                return (
                                                    <React.Fragment key={seller.id}>
                                                        <tr className={expanded ? 'auditoria-sellers__row--expanded' : undefined}>
                                                            <td>
                                                                <div className="auditoria-publicacion__period">
                                                                    <strong>{formatTextValue(resumen.razonSocial)}</strong>
                                                                    <span>ID #{resumen.idSeller ?? '-'}</span>
                                                                </div>
                                                            </td>
                                                            <td>{formatTextValue(resumen.cuit)}</td>
                                                            <td>
                                                                {resumen.estadoActual ? (
                                                                    <span className={getEstadoPillClass(resumen.estadoActual)}>
                                                                        {resumen.estadoActual}
                                                                    </span>
                                                                ) : (
                                                                    <span className="auditoria-publicacion__muted">Sin estado</span>
                                                                )}
                                                            </td>
                                                            <td>{formatTextValue(resumen.integradorActual)}</td>
                                                            <td>
                                                                <strong>{formatInteger(resumen.cantidadMovimientos)}</strong>
                                                            </td>
                                                            <td>{formatDateTime(resumen.fechaUltimoMovimiento)}</td>
                                                            <td className="text-end">
                                                                <button
                                                                    type="button"
                                                                    className="auditoria-publicacion__toggle"
                                                                    onClick={() => toggleSeller(seller.id)}
                                                                >
                                                                    {expanded ? 'Ocultar' : 'Ver detalle'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {expanded && (
                                                            <tr className="auditoria-publicacion__detail-row">
                                                                <td colSpan="7">
                                                                    <div className="auditoria-sellers__detail-panel">
                                                                        <div className="auditoria-sellers__detail-meta">
                                                                            <div>
                                                                                <span className="auditoria-publicacion__filtro-label">Usuario actual</span>
                                                                                <div className="auditoria-sellers__meta-value">
                                                                                    <strong>{formatTextValue(resumen.usuarioActual)}</strong>
                                                                                    {resumen.emailUsuarioActual ? (
                                                                                        <span className="auditoria-publicacion__muted">
                                                                                            {resumen.emailUsuarioActual}
                                                                                        </span>
                                                                                    ) : null}
                                                                                </div>
                                                                            </div>
                                                                            <div>
                                                                                <span className="auditoria-publicacion__filtro-label">Período consultado</span>
                                                                                <div className="auditoria-sellers__meta-value">
                                                                                    <strong>{formatDateTime(resumen.fechaDesde)}</strong>
                                                                                    <span className="auditoria-publicacion__muted">
                                                                                        hasta {formatDateTime(resumen.fechaHasta)}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            {resumen.idSeller != null && (
                                                                                <div className="auditoria-sellers__detail-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="auditoria-publicacion__filter-btn"
                                                                                        onClick={() => navigate(`/Sellers/Editar/${resumen.idSeller}`)}
                                                                                    >
                                                                                        Ir al seller
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        <div className="auditoria-publicacion__metrics auditoria-sellers__detail-metrics">
                                                                            {sellerDetailCards.map((card) => (
                                                                                <div key={card.label} className="auditoria-publicacion__card">
                                                                                    <strong>{card.value}</strong>
                                                                                    <span className="auditoria-publicacion__label">{card.label}</span>
                                                                                </div>
                                                                            ))}
                                                                        </div>

                                                                        {periodos.length === 0 ? (
                                                                            <div className="auditoria-publicacion__muted">
                                                                                Este seller no tiene períodos con movimientos en el rango consultado.
                                                                            </div>
                                                                        ) : (
                                                                            <div className="auditoria-sellers__periodos-card">
                                                                                <div className="auditoria-publicacion__results-header">
                                                                                    <h6 className="auditoria-publicacion__results-title mb-0">
                                                                                        Movimientos por período
                                                                                    </h6>
                                                                                    <span className="auditoria-publicacion__results-badge">
                                                                                        {periodos.length} períodos
                                                                                    </span>
                                                                                </div>
                                                                                <div className="table-responsive">
                                                                                    <table className="table auditoria-publicacion__events-table mb-0">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th scope="col">
                                                                                                    <HeaderWithTooltip label="Período" tooltip={sellerHeaderTooltips.periodo} />
                                                                                                </th>
                                                                                                <th scope="col">
                                                                                                    <HeaderWithTooltip label="Movimientos" tooltip={sellerHeaderTooltips.movimientos} />
                                                                                                </th>
                                                                                                <th scope="col">
                                                                                                    <HeaderWithTooltip label="Campos modificados" tooltip={sellerHeaderTooltips.camposModificados} />
                                                                                                </th>
                                                                                                <th scope="col">
                                                                                                    <HeaderWithTooltip label="Usuarios" tooltip={sellerHeaderTooltips.usuarios} />
                                                                                                </th>
                                                                                                <th scope="col" className="text-end">Eventos</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {periodos.map((periodo) => {
                                                                                                const periodKey = `${seller.id}-${periodo.id}`;
                                                                                                const periodExpanded = expandedPeriodId === periodKey;

                                                                                                return (
                                                                                                    <React.Fragment key={periodKey}>
                                                                                                        <tr>
                                                                                                            <td>
                                                                                                                <div className="auditoria-publicacion__period">
                                                                                                                    <strong>{formatReadablePeriodDate(periodo.fechaDesde)}</strong>
                                                                                                                    <span>hasta {formatReadablePeriodDate(periodo.fechaHasta)}</span>
                                                                                                                </div>
                                                                                                            </td>
                                                                                                            <td>{formatInteger(periodo.cantidadMovimientos)}</td>
                                                                                                            <td>
                                                                                                                <div className="auditoria-publicacion__alerts">
                                                                                                                    {periodo.camposModificados?.length
                                                                                                                        ? periodo.camposModificados.map((campo) => (
                                                                                                                            <span
                                                                                                                                key={`${periodo.id}-${campo}`}
                                                                                                                                className="auditoria-publicacion__badge"
                                                                                                                            >
                                                                                                                                {campo}
                                                                                                                            </span>
                                                                                                                        ))
                                                                                                                        : (
                                                                                                                            <span className="auditoria-publicacion__muted">
                                                                                                                                Sin cambios identificados
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                </div>
                                                                                                            </td>
                                                                                                            <td>
                                                                                                                <div className="auditoria-publicacion__orders">
                                                                                                                    {periodo.usuarios?.length
                                                                                                                        ? periodo.usuarios.map((usuario) => (
                                                                                                                            <span
                                                                                                                                key={`${periodo.id}-${usuario}`}
                                                                                                                                className="auditoria-publicacion__order-badge"
                                                                                                                            >
                                                                                                                                {usuario}
                                                                                                                            </span>
                                                                                                                        ))
                                                                                                                        : (
                                                                                                                            <span className="auditoria-publicacion__muted">
                                                                                                                                Sin usuario informado
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                </div>
                                                                                                            </td>
                                                                                                            <td className="text-end">
                                                                                                                <button
                                                                                                                    type="button"
                                                                                                                    className="auditoria-publicacion__toggle"
                                                                                                                    onClick={() => togglePeriodo(periodKey)}
                                                                                                                >
                                                                                                                    {periodExpanded ? 'Ocultar eventos' : 'Ver eventos'}
                                                                                                                </button>
                                                                                                            </td>
                                                                                                        </tr>
                                                                                                        {periodExpanded && (
                                                                                                            <tr className="auditoria-publicacion__detail-row">
                                                                                                                <td colSpan="5">
                                                                                                                    {periodo.eventos?.length ? (
                                                                                                                        <div className="auditoria-sellers__events">
                                                                                                                            <div className="auditoria-sellers__events-header">
                                                                                                                                <span className="auditoria-sellers__events-title">
                                                                                                                                    Detalle de eventos
                                                                                                                                </span>
                                                                                                                                <span className="auditoria-publicacion__results-badge">
                                                                                                                                    {periodo.eventos.length} evento{periodo.eventos.length !== 1 ? 's' : ''}
                                                                                                                                </span>
                                                                                                                            </div>
                                                                                                                            <div className="auditoria-sellers__events-list">
                                                                                                                                {periodo.eventos.map((evento) => (
                                                                                                                                    <SellerEventCard
                                                                                                                                        key={evento.id}
                                                                                                                                        evento={evento}
                                                                                                                                    />
                                                                                                                                ))}
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    ) : (
                                                                                                                        <div className="auditoria-publicacion__muted">
                                                                                                                            Este período no trajo eventos detallados.
                                                                                                                        </div>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        )}
                                                                                                    </React.Fragment>
                                                                                                );
                                                                                            })}
                                                                                        </tbody>
                                                                                    </table>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div className="auditoria-publicacion__pagination-wrap">
                                <SafeReactPaginate
                                    previousLabel="Anterior"
                                    nextLabel="Siguiente"
                                    breakLabel="..."
                                    breakClassName="break-me"
                                    pageCount={totalPages}
                                    marginPagesDisplayed={2}
                                    pageRangeDisplayed={5}
                                    onPageChange={handlePageClick}
                                    containerClassName="pagination auditoria-publicacion__pagination mb-0"
                                    subContainerClassName="pages pagination"
                                    activeClassName="active"
                                    forcePage={currentPage - 1}
                                    disableInitialCallback
                                />
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
}

export default AuditoriaSellers;
